// src/app/pages/expediente/expediente-digital/sections/paraclinicos/paraclinicos-page/components/tabla-resultados-laboratorio/tabla-resultados-laboratorio.component.ts

import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { ResultadoExtraido } from '../../../../../../../../interfaces/laboratorio/resultado-laboratorio.interface';
import { ParametroLaboratorio } from '../../../../../../../../interfaces/laboratorio/parametro-laboratorio.interface';

@Component({
  selector: 'app-tabla-resultados-laboratorio',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule],
  templateUrl: './tabla-resultados-laboratorio.component.html',
  styleUrls: ['./tabla-resultados-laboratorio.component.scss'],
})
export class TablaResultadosLaboratorioComponent implements OnInit {
  @Input() resultados: ResultadoExtraido[] = [];
  @Input() parametrosCatalogo: ParametroLaboratorio[] = [];
  @Input() editable: boolean = true;

  @Output() guardar = new EventEmitter<ResultadoExtraido[]>();
  @Output() cancelar = new EventEmitter<void>();

  // Copia de trabajo de los resultados
  resultadosEditables: ResultadoExtraido[] = [];

  ngOnInit() {
    // Crear copia profunda para edición
    this.resultadosEditables = JSON.parse(JSON.stringify(this.resultados));
  }

  /**
   * Obtener parámetro del catálogo
   */
  getParametro(parametroId: number): ParametroLaboratorio | undefined {
    return this.parametrosCatalogo.find((p) => p.id === parametroId);
  }

  /**
   * Obtener rango normal como string
   */
  getRangoNormal(resultado: ResultadoExtraido): string {
    const parametro = this.getParametro(resultado.parametroId);

    if (!parametro) return '-';

    if (parametro.rangoMin !== undefined && parametro.rangoMax !== undefined) {
      return `${parametro.rangoMin} - ${parametro.rangoMax}`;
    }

    if (parametro.valoresNormales && parametro.valoresNormales.length > 0) {
      return parametro.valoresNormales.join(', ');
    }

    return '-';
  }

  /**
   * Obtener estado del valor (Normal, Alto, Bajo)
   */
  getEstadoTexto(resultado: ResultadoExtraido): string {
    if (!resultado.fueraRango) return 'Normal';

    return resultado.nivelAlerta === 'alto' ? 'Alto' : 'Bajo';
  }

  /**
   * Obtener clase CSS para el estado
   */
  getEstadoClass(resultado: ResultadoExtraido): string {
    if (!resultado.fueraRango) {
      return 'badge-normal';
    }

    return resultado.nivelAlerta === 'alto' ? 'badge-alto' : 'badge-bajo';
  }

  /**
   * Obtener clase para fila completa
   */
  getFilaClass(resultado: ResultadoExtraido): string {
    if (resultado.fueraRango) {
      return 'fila-alerta';
    }
    return '';
  }

  /**
   * Manejar cambio de valor
   */
  onValorChange(resultado: ResultadoExtraido) {
    // Recalcular si está fuera de rango
    const parametro = this.getParametro(resultado.parametroId);

    if (parametro && parametro.tipo === 'numerico' && typeof resultado.valor === 'number') {
      if (parametro.rangoMin !== undefined && resultado.valor < parametro.rangoMin) {
        resultado.fueraRango = true;
        resultado.nivelAlerta = 'bajo';
      } else if (parametro.rangoMax !== undefined && resultado.valor > parametro.rangoMax) {
        resultado.fueraRango = true;
        resultado.nivelAlerta = 'alto';
      } else {
        resultado.fueraRango = false;
        resultado.nivelAlerta = 'normal';
      }
    }
  }

  /**
   * Guardar resultados
   */
  onGuardar() {
    this.guardar.emit(this.resultadosEditables);
  }

  /**
   * Cancelar edición
   */
  onCancelar() {
    this.cancelar.emit();
  }

  /**
   * Calcular confianza promedio
   */
  getConfianzaPromedio(): number {
    if (this.resultadosEditables.length === 0) return 0;

    const suma = this.resultadosEditables.reduce((acc, r) => acc + (r.confianza || 0), 0);
    return Math.round((suma / this.resultadosEditables.length) * 100);
  }

  /**
   * Contar resultados fuera de rango
   */
  getCountFueraRango(): number {
    return this.resultadosEditables.filter((r) => r.fueraRango).length;
  }
}
